import React, { useCallback, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';
import styled from 'styled-components';

import useErnie from 'shared/hooks/use-ernie';

import { CmsEditorFrame } from 'src/components/cms-editor-frame';

import { toWebStudioTabKey } from '../tab-keys';
import { useDefaultEnterpriseCName } from './use-default-enterprise-cname';

export default function WebStudioEditor(): JSX.Element {
  const { enterpriseId, modelId } = useParams<{ enterpriseId: string; modelId: string }>();
  const history = useHistory();
  // `tab` is set by the list page that linked in here (see pages-list.tsx /
  // menu-landing-list.tsx) so navigating back can return to that same tab
  // instead of always resetting Web Studio to its first tab.
  const [{ variantKey, tab }, setQueryParams] = useQueryParams({ variantKey: StringParam, tab: StringParam });
  // The CMS editor always needs a store cName to preview against; default it from
  // the enterprise. Hold off mounting the frame until it resolves so the iframe
  // isn't loaded once without a cName and then reloaded once it arrives.
  const { cName, loading } = useDefaultEnterpriseCName(enterpriseId);
  const showErnie = useErnie();

  // Without this, a cms:error from the embedded editor (e.g. an auth failure on
  // save) reaches CmsEditorFrame's onError with no listener attached and is
  // silently dropped -- the user sees a failed save with no explanation.
  const handleEditorError = useCallback(
    (payload?: Record<string, unknown>) => {
      const message = typeof payload?.message === 'string' ? payload.message : 'Something went wrong in the editor.';
      showErnie(message, 'danger');
    },
    [showErnie]
  );

  // CmsEditorFrame derives its iframe `src` from `variantKey`, so simply forwarding
  // the live query param would hard-reload the freshly-authenticated iframe the
  // instant handlePublished rewrites the URL below. Freeze the value actually fed
  // to the iframe and only let it change when we navigate to a different model —
  // the URL itself can move ahead of it, so a page refresh still lands on the
  // correct variant.
  const iframeVariantKeyRef = useRef(variantKey);
  const prevModelIdRef = useRef(modelId);
  if (prevModelIdRef.current !== modelId) {
    prevModelIdRef.current = modelId;
    iframeVariantKeyRef.current = variantKey;
  }

  // Mirrors the iframe's own URL update: when a brand-new object is saved for
  // the first time, the editor generates a variantKey and rewrites its own URL
  // to include it (see StandalonePageEditor's doPublish). The parent frame's URL
  // must follow suit — otherwise refreshing the admin page reloads the iframe
  // without a variantKey, and the newly-created content fails to load.
  const handlePublished = useCallback(
    (payload?: Record<string, unknown>) => {
      const publishedVariantKey = payload?.variantKey;
      if (typeof publishedVariantKey === 'string' && publishedVariantKey !== variantKey) {
        setQueryParams({ variantKey: publishedVariantKey }, 'replaceIn');
      }
    },
    [variantKey, setQueryParams]
  );

  // The embedded editor's logo mark posts a `cms:navigate` message when clicked
  // (see PuckHeaderWithLogo in marketplace), asking the host to send the user
  // back to the Web Studio landing page for this enterprise. The message payload
  // doesn't currently carry a destination -- there's only one place to go back to
  // -- so we ignore it and rely on this page's own `tab` query param (set by
  // whichever list page opened the editor) to land back on the right tab.
  const returnTabKey = toWebStudioTabKey(tab);
  const handleEditorNavigate = useCallback(() => {
    const search = returnTabKey ? `?tab=${returnTabKey}` : '';
    history.push(`/enterprise/${enterpriseId}/web-studio${search}`);
  }, [history, enterpriseId, returnTabKey]);

  return (
    <EditorPage>
      {!loading && (
        <CmsEditorFrame
          enterpriseId={enterpriseId}
          modelId={modelId}
          variantKey={iframeVariantKeyRef.current ?? undefined}
          cName={cName}
          onError={handleEditorError}
          onPublished={handlePublished}
          onNavigate={handleEditorNavigate}
        />
      )}
    </EditorPage>
  );
}

const EditorPage = styled.div`
  height: 100vh;
  width: 100vw;
  overflow: hidden;
`;
